import { zoning_indice_sources, ZoningMetricSources } from '@popety-io/types'

export const SOURCE_PRIORITY: zoning_indice_sources[] = [
  'geoportal',
  'llm',
  'estimate',
]

/** Check if a source entry has any useful data (value, additional_info, or reference) */
const hasSourceData = (entry?: {
  value?: number | null
  additional_info?: string | null
  reference?: any[]
}): boolean => {
  if (!entry) return false
  if (entry.value != null) return true
  if (entry.additional_info) return true
  if (entry.reference && entry.reference.length > 0) return true
  return false
}

/** Get list of source keys that have data, with ALG suppression.
 * A source is included if it has a value, additional_info, or references.
 * ALG (estimate) is hidden when REG (llm) or GEO (geoportal) exists.
 * MAN (manual) is excluded — handled separately via isManualOverride in renderTabs. */
export const getAvailableSources = (
  sources?: ZoningMetricSources
): zoning_indice_sources[] => {
  if (!sources) return []
  const available: zoning_indice_sources[] = []
  const hasLlm = hasSourceData(sources.llm)
  const hasGeoportal = hasSourceData(sources.geoportal)
  // ALG suppression: only include estimate when no authoritative source exists
  if (hasSourceData(sources.estimate) && !hasLlm && !hasGeoportal)
    available.push('estimate')
  if (hasLlm) available.push('llm')
  if (hasGeoportal) available.push('geoportal')
  return available
}

/** Type-safe accessor for ZoningMetricSources by dynamic key */
export const getSourceData = (
  sources: ZoningMetricSources | undefined,
  source: zoning_indice_sources
) => {
  if (!sources) return undefined
  switch (source) {
    case 'estimate':
      return sources.estimate
    case 'llm':
      return sources.llm
    case 'geoportal':
      return sources.geoportal
    case 'manual':
      return sources.manual
    default:
      return undefined
  }
}

/** Get the non-manual source with highest priority (for override scenarios) */
export const getOriginalSource = (
  sources?: ZoningMetricSources
): zoning_indice_sources | undefined => {
  if (!sources) return undefined
  for (const src of SOURCE_PRIORITY) {
    if (getSourceData(sources, src)?.value !== undefined) return src
  }
  return undefined
}

/** Get the value for a specific source */
export const getSourceValue = (
  sources: ZoningMetricSources | undefined,
  source: zoning_indice_sources
): number | undefined => {
  return getSourceData(sources, source)?.value
}

/** Authoritative = llm or geoportal (not estimate) */
export const isAuthoritative = (source: zoning_indice_sources): boolean =>
  source === 'llm' || source === 'geoportal'

/**
 * Always show split layout for consistency across source tabs.
 * Right panel content varies: PDF for REG, map for GEO, estimate stats for ALG.
 */
export const shouldShowRightPanel = (
  _activeTab: zoning_indice_sources | null,
  sources?: ZoningMetricSources
): boolean => {
  if (!sources) return false
  return true
}

/**
 * Determines what type of content the right panel should show.
 * - REG with document: PDF viewer
 * - GEO with link: empty state with external link
 * - Otherwise: generic empty state
 */
export type RightPanelContent = 'pdf' | 'map' | 'estimate' | 'manual' | 'empty'

export const getRightPanelContent = (
  activeTab: zoning_indice_sources | null,
  sources?: ZoningMetricSources
): RightPanelContent => {
  if (activeTab === 'manual') {
    return 'manual'
  }
  if (activeTab === 'llm' && sources?.llm?.reference?.[0]?.document_url) {
    return 'pdf'
  }
  if (activeTab === 'geoportal') {
    return 'map'
  }
  if (activeTab === 'estimate') {
    return 'estimate'
  }
  return 'empty'
}

export const parseMetricKey = (
  metricKey: string
): { metricColumn: string; metricBound: 'min' | 'max' | 'absolute' } => {
  if (metricKey.endsWith('_max')) {
    return { metricColumn: metricKey.replace(/_max$/, ''), metricBound: 'max' }
  }
  if (metricKey.endsWith('_min')) {
    return { metricColumn: metricKey.replace(/_min$/, ''), metricBound: 'min' }
  }
  return { metricColumn: metricKey, metricBound: 'absolute' }
}
